/**
 * Feb 2026 · Customer Master — bespoke dialog.
 *
 * The generic SCHEMA-driven form in Masters.jsx can't model the new
 * repeatable `addresses[]` list (Bill To / Ship To / Warehouse / Registered
 * Office / Other) or the Region dropdown fed by GET /api/customer-regions.
 * This component handles create + edit end-to-end and posts the payload
 * directly to /customers.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { PlusIcon, TrashIcon, StarIcon, WarningIcon, CheckCircleIcon } from "@phosphor-icons/react";

const inp = "w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = inp;
const area = "w-full min-h-[64px] px-2.5 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] resize-y";

// Feb 25, 2026 · India GSTIN state-code → state-name map (official
// codes 01-38 published by GSTN). Powers GSTIN auto-fill so a clean
// paste populates State + State Code without the user retyping.
const GST_STATE_CODES = {
  "01": "JAMMU & KASHMIR", "02": "HIMACHAL PRADESH", "03": "PUNJAB",
  "04": "CHANDIGARH", "05": "UTTARAKHAND", "06": "HARYANA",
  "07": "DELHI", "08": "RAJASTHAN", "09": "UTTAR PRADESH",
  "10": "BIHAR", "11": "SIKKIM", "12": "ARUNACHAL PRADESH",
  "13": "NAGALAND", "14": "MANIPUR", "15": "MIZORAM",
  "16": "TRIPURA", "17": "MEGHALAYA", "18": "ASSAM",
  "19": "WEST BENGAL", "20": "JHARKHAND", "21": "ODISHA",
  "22": "CHHATTISGARH", "23": "MADHYA PRADESH", "24": "GUJARAT",
  "25": "DAMAN & DIU", "26": "DADRA & NAGAR HAVELI",
  "27": "MAHARASHTRA", "28": "ANDHRA PRADESH (OLD)",
  "29": "KARNATAKA", "30": "GOA", "31": "LAKSHADWEEP",
  "32": "KERALA", "33": "TAMIL NADU", "34": "PUDUCHERRY",
  "35": "ANDAMAN & NICOBAR", "36": "TELANGANA",
  "37": "ANDHRA PRADESH", "38": "LADAKH",
};

// Feb 25, 2026 · India GSTIN structural regex.
//   Slots 1-2   → state code (digits)
//   Slots 3-7   → PAN prefix (five letters)
//   Slots 8-11  → PAN sequence (four digits)
//   Slot  12    → PAN check letter
//   Slot  13    → entity number (1-9, A-Z)
//   Slot  14    → literal "Z"
//   Slot  15    → check digit (0-9, A-Z)
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const isValidGstin = (g) => GSTIN_RE.test((g || "").trim().toUpperCase());

const uid = () => (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));

const emptyAddress = (label = "Bill To") => ({
  id: uid(),
  label,
  custom_label: "",
  address: "",
  city: "",
  state: "",
  state_code: "",
  pincode: "",
  country: "",
  gstin: "",
  // Feb 25, 2026 · PAN — auto-extracted from GSTIN slots 3-12.
  pan: "",
  contact_person: "",
  phone: "",
  email: "",
  is_primary_bill: false,
  is_primary_ship: false,
});

const emptyCustomer = () => ({
  code: "",
  name: "",
  contact_person: "",
  email: "",
  phone: "",
  country: "",
  region: "",
  // Feb 2026 · Payment Terms — free text (uses PAYMENT_TERM_PRESETS datalist
  // for one-click picks); credit_days feeds aging / receivable reports.
  payment_terms: "",
  credit_days: 0,
  addresses: [{ ...emptyAddress("Bill To"), is_primary_bill: true, is_primary_ship: true }],
});

// Presets surfaced through a <datalist> so the input stays a free-text
// field but planners can pick a standard term in one click. Extend at
// will — the field is a plain string on the wire.
const PAYMENT_TERM_PRESETS = [
  "Advance",
  "COD (Cash on Delivery)",
  "Net 15",
  "Net 30",
  "Net 45",
  "Net 60",
  "Net 90",
  "50% Advance / 50% on Delivery",
  "30% Advance / 70% on Dispatch",
  "LC at Sight",
  "LC 30 Days",
  "TT in Advance",
];

// Auto-derive credit_days from a preset when the user picks one so the
// aging report has a machine-readable number without a second field-fill.
const _daysFromTerm = (t) => {
  const m = /net\s*(\d+)/i.exec(t || "");
  if (m) return Number(m[1]);
  if (/^advance$/i.test(t) || /^cod/i.test(t) || /tt in advance/i.test(t)) return 0;
  return null;  // ambiguous — leave whatever the user entered
};

export default function CustomerFormDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(emptyCustomer());
  const [regions, setRegions] = useState([]);
  const [labels, setLabels] = useState(["Bill To", "Ship To", "Warehouse", "Registered Office", "Other"]);
  const [saving, setSaving] = useState(false);
  // Feb 25, 2026 · Duplicate-GSTIN warnings, keyed by address index.
  // Each entry is `{ code, name, address_label }` when a match is
  // found on ANOTHER customer, else absent. Cleared on save.
  const [dupWarn, setDupWarn] = useState({});

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [r, l] = await Promise.all([
          api.get("/customer-regions"),
          api.get("/customer-address-labels"),
        ]);
        setRegions(r.data?.regions || []);
        setLabels(l.data?.labels || labels);
      } catch (e) { console.warn("region/label fetch failed", e); }
      // Prime the form
      if (editing) {
        // Deep clone so edits stay local until Save
        const clone = JSON.parse(JSON.stringify(editing));
        if (!Array.isArray(clone.addresses) || clone.addresses.length === 0) {
          clone.addresses = [{ ...emptyAddress("Bill To"), is_primary_bill: true, is_primary_ship: true }];
        } else {
          clone.addresses = clone.addresses.map(a => ({ ...emptyAddress(), ...a, id: a.id || uid() }));
        }
        setForm({ ...emptyCustomer(), ...clone });
      } else {
        // New customer — fetch next code
        try {
          const { data } = await api.get("/next/customer-code");
          setForm({ ...emptyCustomer(), code: data?.code || "" });
        } catch (e) {
          setForm(emptyCustomer());
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const updateAddr = (idx, patch) => {
    setForm(f => {
      const next = [...f.addresses];
      next[idx] = { ...next[idx], ...patch };
      return { ...f, addresses: next };
    });
  };

  const setPrimary = (idx, kind /* "bill" | "ship" */) => {
    setForm(f => {
      const flag = kind === "bill" ? "is_primary_bill" : "is_primary_ship";
      const next = f.addresses.map((a, i) => ({ ...a, [flag]: i === idx }));
      return { ...f, addresses: next };
    });
  };

  const addAddress = () => {
    setForm(f => ({ ...f, addresses: [...f.addresses, emptyAddress("Ship To")] }));
  };

  const removeAddress = (idx) => {
    setForm(f => {
      const next = f.addresses.filter((_, i) => i !== idx);
      // If we removed the last primary bill/ship, promote the first surviving row.
      const noBill = !next.some(a => a.is_primary_bill);
      const noShip = !next.some(a => a.is_primary_ship);
      if (next.length > 0) {
        if (noBill) next[0].is_primary_bill = true;
        if (noShip) next[0].is_primary_ship = true;
      }
      return { ...f, addresses: next };
    });
  };

  const primaryBillLabel = useMemo(() => {
    const p = form.addresses.find(a => a.is_primary_bill);
    if (!p) return "";
    return p.label === "Other" ? (p.custom_label || "Other") : p.label;
  }, [form.addresses]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Customer Name is required"); return; }
    if (!form.code?.trim()) { toast.error("Customer Code is required"); return; }
    // Ensure at least one address is primary bill + primary ship
    const hasPrimaryBill = form.addresses.some(a => a.is_primary_bill);
    const hasPrimaryShip = form.addresses.some(a => a.is_primary_ship);
    if (form.addresses.length > 0 && (!hasPrimaryBill || !hasPrimaryShip)) {
      toast.error("Mark one address as Primary Bill and Primary Ship");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers/", payload);
        toast.success("Customer created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="customer-dialog" description="Add or edit a customer — GSTIN, PAN, primary contact and multiple Bill-To / Ship-To / Warehouse addresses.">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Edit" : "New"} Customer</DialogTitle>
        </DialogHeader>

        {/* Header fields */}
        <div className="grid grid-cols-6 gap-3">
          <div className="col-span-1">
            <div className="overline mb-1.5">Code</div>
            <input data-testid="customer-code" value={form.code} readOnly className={`${inp} opacity-75 font-mono cursor-not-allowed`} title="Auto-generated" />
          </div>
          <div className="col-span-2">
            <div className="overline mb-1.5">Customer Name *</div>
            <input data-testid="customer-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inp} />
          </div>
          <div className="col-span-1">
            <div className="overline mb-1.5">Contact</div>
            <input data-testid="customer-contact" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} className={inp} />
          </div>
          <div className="col-span-1">
            <div className="overline mb-1.5">Email</div>
            <input data-testid="customer-email" data-nouppercase value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inp} />
          </div>
          <div className="col-span-1">
            <div className="overline mb-1.5">Phone</div>
            <input data-testid="customer-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inp} />
          </div>
          <div className="col-span-1">
            <div className="overline mb-1.5">Country</div>
            <input data-testid="customer-country" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} className={inp} />
          </div>
          <div className="col-span-2">
            <div className="overline mb-1.5">Region</div>
            <Select value={form.region || ""} onValueChange={v => setForm({ ...form, region: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="customer-region" className={selCls}><SelectValue placeholder="Pick a region…" /></SelectTrigger>
              <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                <SelectItem value="__none__">— None —</SelectItem>
                {regions.map(r => <SelectItem key={r} value={r} data-testid={`customer-region-opt-${r}`}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Feb 2026 · Payment Terms + Credit Days. Datalist gives one-tap
              presets while keeping the field open for anything custom. */}
          <div className="col-span-2">
            <div className="overline mb-1.5">Payment Terms</div>
            <input
              data-testid="customer-payment-terms"
              list="customer-payment-term-presets"
              value={form.payment_terms || ""}
              onChange={e => {
                const v = e.target.value;
                const days = _daysFromTerm(v);
                setForm(f => ({ ...f, payment_terms: v, credit_days: days ?? f.credit_days }));
              }}
              placeholder="e.g. Net 30 · 50% Advance"
              className={inp}
            />
            <datalist id="customer-payment-term-presets">
              {PAYMENT_TERM_PRESETS.map(p => <option key={p} value={p} />)}
            </datalist>
          </div>
          <div className="col-span-1">
            <div className="overline mb-1.5">Credit Days</div>
            <input
              data-testid="customer-credit-days"
              type="number"
              min="0"
              value={form.credit_days ?? 0}
              onChange={e => setForm({ ...form, credit_days: Math.max(0, Number(e.target.value) || 0) })}
              className={`${inp} text-right tabular-nums`}
            />
          </div>
          <div className="col-span-3 flex items-end text-[11px] text-zinc-500 pb-1">
            <span>Primary Bill To: <span className="text-white font-semibold" data-testid="customer-primary-bill-label">{primaryBillLabel || "— set below —"}</span></span>
          </div>
        </div>

        {/* Addresses repeater */}
        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="overline">Addresses ({form.addresses.length})</div>
            <button
              type="button"
              onClick={addAddress}
              data-testid="customer-add-address"
              className="h-8 px-3 rounded-sm border border-[var(--accent)]/60 text-[var(--accent)] text-xs font-semibold flex items-center gap-1.5 hover:bg-[var(--accent)]/10"
            >
              <PlusIcon size={12} weight="bold" />Add Address
            </button>
          </div>

          {form.addresses.map((a, idx) => (
            <div key={a.id} className="tactical-card p-3 space-y-3 border border-[var(--border)]" data-testid={`customer-address-${idx}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-40">
                  <div className="overline mb-1">Label</div>
                  <Select value={a.label} onValueChange={v => updateAddr(idx, { label: v })}>
                    <SelectTrigger data-testid={`addr-label-${idx}`} className={selCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                      {labels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {a.label === "Other" && (
                  <div className="w-56">
                    <div className="overline mb-1">Custom Label</div>
                    <input value={a.custom_label} onChange={e => updateAddr(idx, { custom_label: e.target.value })} className={inp} placeholder="e.g. Head Office" data-testid={`addr-custom-label-${idx}`} />
                  </div>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    type="button"
                    onClick={() => setPrimary(idx, "bill")}
                    data-testid={`addr-set-primary-bill-${idx}`}
                    className={`h-8 px-3 rounded-sm border text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${a.is_primary_bill ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-[var(--border)] text-zinc-400 hover:bg-[var(--bg-2)]"}`}
                    title="Use this as the default Bill To when creating orders"
                  >
                    <StarIcon size={11} weight={a.is_primary_bill ? "fill" : "regular"} />Primary Bill
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrimary(idx, "ship")}
                    data-testid={`addr-set-primary-ship-${idx}`}
                    className={`h-8 px-3 rounded-sm border text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${a.is_primary_ship ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-200" : "border-[var(--border)] text-zinc-400 hover:bg-[var(--bg-2)]"}`}
                    title="Use this as the default Ship To when creating orders"
                  >
                    <StarIcon size={11} weight={a.is_primary_ship ? "fill" : "regular"} />Primary Ship
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAddress(idx)}
                    data-testid={`addr-remove-${idx}`}
                    className="h-8 w-8 rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center justify-center"
                    title="Remove this address"
                  >
                    <TrashIcon size={12} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-6">
                  <div className="overline mb-1">Address (multi-line)</div>
                  <textarea value={a.address} onChange={e => updateAddr(idx, { address: e.target.value })} className={area} data-testid={`addr-address-${idx}`} placeholder="Building / Street / Locality" />
                </div>
                <div className="col-span-2">
                  <div className="overline mb-1">City</div>
                  <input value={a.city} onChange={e => updateAddr(idx, { city: e.target.value })} className={inp} data-testid={`addr-city-${idx}`} />
                </div>
                <div className="col-span-2">
                  <div className="overline mb-1">State</div>
                  <input value={a.state} onChange={e => updateAddr(idx, { state: e.target.value })} className={inp} data-testid={`addr-state-${idx}`} />
                </div>
                <div className="col-span-1">
                  <div className="overline mb-1">State Code<span className="text-zinc-500 text-[9px] normal-case ml-1">(2-digit)</span></div>
                  <input
                    value={a.state_code || ""}
                    onChange={e => updateAddr(idx, { state_code: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) })}
                    className={`${inp} font-mono`}
                    placeholder="27"
                    maxLength={2}
                    inputMode="numeric"
                    data-testid={`addr-state-code-${idx}`}
                  />
                </div>
                <div className="col-span-1">
                  <div className="overline mb-1">Pincode</div>
                  <input value={a.pincode} onChange={e => updateAddr(idx, { pincode: e.target.value })} className={inp} data-testid={`addr-pincode-${idx}`} />
                </div>
                <div className="col-span-1">
                  <div className="overline mb-1">Country</div>
                  <input value={a.country} onChange={e => updateAddr(idx, { country: e.target.value })} className={inp} data-testid={`addr-country-${idx}`} />
                </div>
                <div className="col-span-2">
                  <div className="overline mb-1 flex items-center gap-2">
                    <span>GSTIN (India)</span>
                    {a.gstin && (isValidGstin(a.gstin)
                      ? <CheckCircleIcon size={11} weight="fill" className="text-emerald-400" title="Valid GSTIN structure" />
                      : <WarningIcon    size={11} weight="fill" className="text-red-400"     title="Invalid GSTIN structure" />)}
                  </div>
                  <input
                    value={a.gstin}
                    onChange={e => {
                      const raw = e.target.value.toUpperCase().replace(/\s+/g, "");
                      const patch = { gstin: raw };
                      if (isValidGstin(raw)) {
                        const sc = raw.slice(0, 2);
                        if (!(a.state_code || "").trim()) patch.state_code = sc;
                        if (!(a.state || "").trim() && GST_STATE_CODES[sc]) patch.state = GST_STATE_CODES[sc];
                        // Feb 25, 2026 · PAN sits inside the GSTIN at slots
                        // 3-12 (0-indexed 2..11). Fill silently when empty.
                        if (!(a.pan || "").trim()) patch.pan = raw.slice(2, 12);
                        // Kick off duplicate check against other customers.
                        (async () => {
                          try {
                            const { data } = await api.get("/customers/check-gstin", {
                              params: { gstin: raw, exclude_id: editing?.id || "" },
                            });
                            setDupWarn(prev => {
                              const next = { ...prev };
                              if (data?.exists) next[idx] = data.customer;
                              else delete next[idx];
                              return next;
                            });
                          } catch { /* ignore */ }
                        })();
                      } else {
                        setDupWarn(prev => { const n = { ...prev }; delete n[idx]; return n; });
                      }
                      updateAddr(idx, patch);
                    }}
                    className={`${inp} font-mono ${a.gstin && !isValidGstin(a.gstin) ? "border-red-500/60 focus:border-red-500" : ""}`}
                    placeholder="27AAAAA0000A1Z5"
                    maxLength={15}
                    data-testid={`addr-gstin-${idx}`}
                  />
                  {a.gstin && !isValidGstin(a.gstin) && (
                    <div className="mt-1 flex items-start gap-1 text-[10px] text-red-400" data-testid={`addr-gstin-error-${idx}`}>
                      <WarningIcon size={11} weight="fill" className="mt-0.5 flex-shrink-0" />
                      <span>Invalid GSTIN — must be 15 chars: 2-digit state, 5-letter PAN, 4-digit seq, 1-letter check, entity, "Z", check digit.</span>
                    </div>
                  )}
                  {a.gstin && isValidGstin(a.gstin) && GST_STATE_CODES[a.gstin.slice(0, 2)] && (
                    <div className="mt-1 text-[10px] text-emerald-400" data-testid={`addr-gstin-state-hint-${idx}`}>
                      State Code {a.gstin.slice(0, 2)} · {GST_STATE_CODES[a.gstin.slice(0, 2)]}
                    </div>
                  )}
                  {dupWarn[idx] && (
                    <div
                      className="mt-1 flex items-start gap-1 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-sm px-2 py-1"
                      data-testid={`addr-gstin-dup-${idx}`}
                    >
                      <WarningIcon size={12} weight="fill" className="mt-0.5 flex-shrink-0" />
                      <span>Already registered to <b>{dupWarn[idx].code} · {dupWarn[idx].name}</b> ({dupWarn[idx].address_label}). Duplicate GSTINs can cross-book invoices — verify before saving.</span>
                    </div>
                  )}
                </div>
                <div className="col-span-1">
                  <div className="overline mb-1">PAN <span className="text-zinc-500 text-[9px] normal-case">(auto)</span></div>
                  <input
                    value={a.pan || ""}
                    onChange={e => updateAddr(idx, { pan: e.target.value.toUpperCase().replace(/\s+/g, "").slice(0, 10) })}
                    className={`${inp} font-mono`}
                    placeholder="AAAAA0000A"
                    maxLength={10}
                    data-testid={`addr-pan-${idx}`}
                  />
                </div>
                <div className="col-span-2">
                  <div className="overline mb-1">Contact Person</div>
                  <input value={a.contact_person} onChange={e => updateAddr(idx, { contact_person: e.target.value })} className={inp} data-testid={`addr-contact-${idx}`} />
                </div>
                <div className="col-span-1">
                  <div className="overline mb-1">Phone</div>
                  <input value={a.phone} onChange={e => updateAddr(idx, { phone: e.target.value })} className={inp} data-testid={`addr-phone-${idx}`} />
                </div>
                <div className="col-span-1">
                  <div className="overline mb-1">Email</div>
                  <input value={a.email} data-nouppercase onChange={e => updateAddr(idx, { email: e.target.value })} className={inp} data-testid={`addr-email-${idx}`} />
                </div>
              </div>
            </div>
          ))}

          {form.addresses.length === 0 && (
            <div className="tactical-card p-6 text-center text-xs text-zinc-500">No addresses yet — click "Add Address" to add Bill To / Ship To details.</div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400" disabled={saving}>Cancel</button>
          <button
            data-testid="customer-save"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold text-white"
          >{saving ? "Saving…" : "Save Customer"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
